// What changed in each hub release, for staff: shown when they click the version in the sidebar (shell/Shell.tsx).
// Newest first. Every release needs its entry before it is built: scripts/release.mjs refuses to build a version
// that isn't listed here. Write for the team: plain, professional, about what they will notice.

export interface ReleaseNote {
  /** The release number, exactly as the release is built (e.g. 2026.11.03-11), or a range for a day of small releases. */
  version: string
  /** The day it went live, YYYY-MM-DD. */
  date: string
  /** One line saying what the release is about. */
  title: string
  changes: string[]
}

export const RELEASE_NOTES: ReleaseNote[] = [
  {
    version: '2026.11.03-17',
    date: '2026-11-03',
    title: 'Easier to find: the version and what’s new',
    changes: ['The version at the bottom of the menu is now a clear button, and the “What’s new” window shows its text clearly in front of the page behind it.'],
  },
  {
    version: '2026.11.03-15',
    date: '2026-11-03',
    title: 'The team’s feedback: projects, photos, EPC requests, Kaizen, HR and tasks',
    changes: [
      'The hub version is shown at the bottom of the menu. Click it to see what has changed in each release.',
      'Client work requests now appear at the top of Enquiries with a “Client work request” or “Add plots” label, and the menu shows how many are open. New work requests, enquiries and access requests always email the office (info@ or portal@) with a link straight to them. Requests can be archived once dealt with.',
      'Clients can ask to add plots to one of their existing sites from the portal.',
      'Projects: an “Active site” switch controls whether a site appears in the Kaizen (KZN) site list and SiteScope, as on the old hub. Several sites can be switched at once from the projects list.',
      'Documents tab: the plot grid is back, with a row per plot and a column per report (SAP, PEA, BREL, as-built, TM59, Part O, Part G and model files). Open a cell to view, download or replace a report and see earlier versions. Clients can open these reports for their own plots again.',
      'Batch upload works like the old Master Upload: select a set of PDFs and the plot and report type are worked out from each file name, with a table to check and correct them before anything is saved.',
      'Client managers: add a manager from Contacts or a project’s Client access tab, and manage a manager’s sign-in, details, companies, projects and plots on one page.',
      'Photo review: “Photos waiting” on the projects list now matches the review queue. Old photos carried over from the old hub no longer count.',
      'Photo review: photographic reports open full screen with the approve and reject panel alongside, and show who uploaded them with their email, company and phone. Rejection reasons have a larger box and one-click common reasons.',
      'Photographic report checking reads the report’s “cylinder installed” answer and the SAP’s “Cylinder Installed” tick correctly, so combi plots are no longer expected to show a cylinder.',
      'Client view: the Photos tab is staff-only; each plot shows one button (Report if a report was uploaded, otherwise Photos); clients can delete a photo they uploaded while it’s still waiting to be checked. The Alternative evidence pages are hidden and the Project pack tab is limited to super admins for now.',
      'EPC requests: the client’s answers and the design SAP are shown side by side with differences first; a photographic report is produced from the plot’s photos when none was uploaded; documents still held on the old hub open again; a summary of EPC figures is back at the top of the list.',
      'EPC requests: the hot water cylinder is chosen from a list (Kaizen SAP’s 630 plus 318 more from manufacturers’ datasheets, each with its datasheet), or typed in if not listed, and is required when the SAP has a cylinder. A failed air test no longer counts as the certificate on file.',
      'NHBC: projects marked for NHBC show an NHBC badge and filter on the EPC list and an NHBC panel on each EPC request, where staff can also mark a project. NHBC uploads stay manual for now.',
      'Kaizen SAP: who has a project open is now always up to date. Locks left behind by a closed or crashed Kaizen clear by themselves, staff can see when someone was last active and release a lock, and Kaizen SAP › Open in Kaizen lists everyone with a project open.',
      'HR: each person’s working pattern is shown on the leave form, and leave can’t be booked only on days they don’t work. Leave requests and cancellations now email the holiday approver with the line manager copied in.',
      'Tasks: Status, Assignee, Due and Client filters can each take several values.',
      'Kaizen PSI: sloped roof junctions imported from BISCO files are now read accurately and match the published BISCO results.',
    ],
  },
  {
    version: '2026.11.03-13',
    date: '2026-11-03',
    title: 'Clearer photo declarations for site managers',
    changes: [
      'Signing a photo declaration: on a plot with a photographic report, the page now lists exactly which rejected sections the declaration covers and which still need a new photo, with our note for each. Managers no longer choose sections on these plots.',
      'On plots without a report, only the sections still missing a photo are listed, and none are ticked in advance.',
      'The declaration page now explains when a declaration should be used and links to the plot’s photos page for anyone who can still take a photo.',
    ],
  },
  {
    version: '2026.11.03-11',
    date: '2026-11-03',
    title: 'Photo review safeguard',
    changes: [
      'Photo review: a photo can no longer be rejected when another photo of the same section on that plot is already approved. The review screen shows the approved photo and offers “Approve with the approved photo” instead, so sites aren’t emailed about evidence we already hold. “Reject anyway” remains for a photo of the wrong item or plot.',
      'Rejection emails now always show the full reason.',
    ],
  },
  {
    version: '2026.11.03-10',
    date: '2026-11-03',
    title: 'Kaizen PSI improvements',
    changes: [
      'Kaizen PSI: drawings can now be read automatically to suggest the material of each area and the junction type. Each suggestion stays marked for checking until someone confirms it, and results can’t be sent to SAP before that.',
      'Kaizen PSI: a Flanking elements panel is always on screen, showing each flank’s U-value and lengths.',
      'Kaizen PSI: corners, jambs and party walls now correctly use wall surface resistance on every face, and the example junctions open with their SAP codes set.',
    ],
  },
  {
    version: '2026.11.03-9',
    date: '2026-11-03',
    title: 'Kaizen PSI launched',
    changes: [
      'New: Kaizen PSI under Tools. Calculate linear thermal transmittance (Ψ) and temperature factor (fRsi) for junctions to BS EN ISO 10211 and BR 497, in the browser.',
      'Projects of junctions are linked to hub projects; import a junction drawing (PDF or image) or draw it, calculate in seconds and produce the PDF report.',
      'Calculated values can be sent straight into the junction table of SAP dwellings, once the calculation is validated and reviewed.',
    ],
  },
  {
    version: '2026.11.03-8',
    date: '2026-11-03',
    title: 'Kaizen Audit',
    changes: ['Kaizen Audit exports now carry the lodging assessor’s number.'],
  },
  {
    version: '2026.11.03-7',
    date: '2026-11-03',
    title: 'Kaizen Audit uploads',
    changes: ['Kaizen Audit: every upload for a project now goes through, not only the first.'],
  },
  {
    version: '2026.11.03-6',
    date: '2026-11-03',
    title: 'Kaizen Audit and bank lines',
    changes: ['Kaizen Audit now shows the assessor for each record.', 'Bank lines no longer show “QuickFile” as their source.'],
  },
  {
    version: '2026.11.03-5',
    date: '2026-11-03',
    title: 'Photographic report checking',
    changes: [
      'Report checking: soil pipes are no longer mistaken for waste water heat recovery units.',
      'Report checking: a cylinder photo can now count towards primary pipework insulation (6e).',
    ],
  },
  {
    version: '2026.11.03-4',
    date: '2026-11-03',
    title: 'Bank feed',
    changes: ['The bank feed now reads every transaction from QuickFile correctly.'],
  },
  {
    version: '2026.11.03-3',
    date: '2026-11-03',
    title: 'Easier-to-read numbers',
    changes: ['Amounts, counts and dates now use the normal font across the hub.'],
  },
  {
    version: '2026.11.03-2',
    date: '2026-11-03',
    title: 'BREL signing',
    changes: ['BREL signing: the client’s name is typed into the Signed box again, as on the old hub.'],
  },
  {
    version: '2026.11.03-1',
    date: '2026-11-03',
    title: 'Dashboard, tasks, HR, documents, bank and chat',
    changes: [
      'Personal dashboard: everyone can choose, order and size their own widgets.',
      'Tasks: quick editing from the list, saved-filter management, feedback, templates, calendar view, weekly summary emails, and part-completion of dependent tasks. Completing a task checks for SAP or TM59 work first.',
      'HR: bank holidays import from GOV.UK and can be edited in place; Scottish staff and notes are supported.',
      'Project documents: batch upload, search and a Part O summary on the Documents tab.',
      'Bank page: allocate payments the same way as on the old hub; unassigned lines are counted in the totals.',
      'Outlook add-in: sign in once, have the project suggested, and file emails to projects and tasks.',
      'Groundwork for the Briary Hub phone app (sign-in, notifications and inbox).',
      'Xero: an update can no longer leave all Xero reading paused.',
    ],
  },
  {
    version: '2026.11.02-1 to 2026.11.02-31',
    date: '2026-11-02',
    title: 'EPC requests, photo checking, HR and staff chat',
    changes: [
      'EPC requests: a compact, sortable queue that fits the page; a check of the specification against the SAP with product matching; a document viewer; and the plot’s photos on the request page.',
      'EPC requests: managers confirm or change the heating and WWHRS, every answer is shown, the address is checked and the product labels in photos are read automatically.',
      'EPC requests: staff can upload an air test or MCS certificate, which is read automatically, and can send a request back to the manager to correct and resubmit.',
      'EPC requests: a warning when the MCS file isn’t a solar PV MCS certificate or PV schedule.',
      'Photographic reports: automatic checking flags heating, cylinder and WWHRS differences from the SAP; staff can tick and override each photo; sections that aren’t required show as neutral; loft (3a) and blown-fibre cavity rules are applied, so a blown-fibre cavity is never failed automatically.',
      'WWHRS photos: a clear photo of the unit is enough; the label is optional.',
      'Photos: older historical photos no longer clutter the review queue, and old PNG photo evidence is accepted.',
      'Plots: “all photos approved” flags carried over incorrectly from the old hub were repaired.',
      'Project page: less-used tabs are grouped under Money and Technical menus, and staff can view the project as the client manager sees it.',
      'Managers: uploads by managers, manager access controls and module settings for each manager.',
      'Staff access: permissions can be set in detail and roles can be duplicated.',
      'SAP: UPRN and project lock; EPC and BREL documents are stamped correctly before submission; the EPC queue opens the right SAP project.',
      'Email templates and signatures, HR analytics, pro-rata holiday allowance for new starters, and editing or removing sickness records.',
      'Bank feed from QuickFile every two hours, with Refresh now and no duplicate lines.',
      'Staff chat: a chat panel in the corner of every page with live messages, groups and file sharing.',
      'Open browser tabs pick up a new release without needing Ctrl+F5.',
      'Short help videos on key pages.',
    ],
  },
]
